import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth-store';
import { SessionProvider } from '@/components/providers/SessionProvider';
import LoginPage from '@/pages/auth/LoginPage';
import DashboardLayout from '@/components/layout/DashboardLayout';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ExecutiveDashboard from '@/pages/executive/ExecutiveDashboard';
import DataSourcesPage from '@/pages/data-sources/DataSourcesPage';
import PipelineDashboard from '@/pages/pipelines/PipelineDashboard';
import PipelineBuilderPage from '@/pages/pipelines/PipelineBuilderPage';
import MonitoringPage from '@/pages/monitoring/MonitoringPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import DimensionManagementPage from '@/pages/admin/dimensions/DimensionManagement';
import AIPipelineGeneratorPage from '@/pages/pipelines/AIPipelineGeneratorPage';
import UsersPage from '@/pages/admin/users/UsersPage';
import IKUPerangkatDaerahPage from '@/pages/public-datasets/IKUPerangkatDaerahPage';
import BnbaDatasetsPage from '@/pages/bnba-datasets/BnbaDatasetsPage';

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin-only route component (redirect to dashboard if not admin)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Public route component (redirect to dashboard if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <SessionProvider>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="executive" element={<ExecutiveDashboard />} />
          <Route path="data-sources" element={<DataSourcesPage />} />
          <Route path="public-datasets/iku-perangkat-daerah" element={<IKUPerangkatDaerahPage />} />
          <Route path="pipelines" element={<PipelineDashboard />} />
          <Route path="pipelines/new" element={<PipelineBuilderPage />} />
          <Route path="pipelines/:id" element={<PipelineBuilderPage />} />
          <Route path="pipelines/ai-generate" element={<AIPipelineGeneratorPage />} />
          <Route path="admin/dimensions" element={<DimensionManagementPage />} />
          <Route
            path="bnba-datasets"
            element={
              <AdminRoute>
                <BnbaDatasetsPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <AdminRoute>
                <UsersPage />
              </AdminRoute>
            }
          />
          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </SessionProvider>
  );
}

export default App;
